import type { GrupoCatalogo, ProdutoCatalogo } from "@/api"
import { memo, useMemo, type ReactNode } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { capitalize, normalizarTexto } from "@/lib/formato"
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'

export type CategoriaSelecionada = number | 'DESTAQUE' | null

interface GradeProdutosProps {
    grupos: GrupoCatalogo[]
    selectedCategoryId: CategoriaSelecionada
    /** Card de cada produto — quem chama liga no carrinho do seu modo. */
    renderCard: (produto: ProdutoCatalogo, cor: string | null) => ReactNode
    /** Quando preenchido, filtra por nome/descrição e ignora a categoria. */
    termoBusca?: string
    /** Cabeçalho da grade (nome da categoria). Some na busca. */
    mostrarTitulo?: boolean
    /**
     * Largura mínima do card. O padrão é o do totem de checkout, que está em
     * produção: quem quiser medida fluida (tablet/totem de pedidos) passa a sua.
     * Sem isso, mexer no tablet mudava a grade do checkout junto.
     */
    larguraMinimaCard?: string
    /** Fonte do nome do grupo; sem ela vale o tamanho do tema. */
    fonteTitulo?: string
    /** Mostra o "Buscar" na linha do título. */
    aoBuscar?: () => void
}

interface ProdutoComCor {
    produto: ProdutoCatalogo
    cor: string | null
}

/** Texto pesquisável do produto (mesmos campos da busca do cardápio). */
function textoBusca(produto: ProdutoCatalogo): string {
    return `${produto.descricao} ${produto.descricaoComposicao ?? ''} ${produto.descricaoCompleta ?? ''} ${produto.descricaoSucinta ?? ''}`
}

function GradeProdutos({ grupos, selectedCategoryId, renderCard, termoBusca, mostrarTitulo = true, larguraMinimaCard = '220px', fonteTitulo, aoBuscar }: GradeProdutosProps): JSX.Element {
    const busca = normalizarTexto((termoBusca ?? '').trim())

    const produtos = useMemo<ProdutoComCor[]>(() => {
        // Busca varre o cardápio inteiro — categoria selecionada não limita.
        const gruposFiltrados = !busca && typeof selectedCategoryId === 'number'
            ? grupos.filter((g) => g.id === selectedCategoryId)
            : grupos
        const vistos = new Set<number>()
        const lista: ProdutoComCor[] = []

        for (const grupo of gruposFiltrados) {
            for (const produto of grupo.produtos) {
                if (vistos.has(produto.id)) continue
                if (!busca && selectedCategoryId === 'DESTAQUE' && !produto.destaqueCardapio) continue
                if (busca && !normalizarTexto(textoBusca(produto)).includes(busca)) continue

                vistos.add(produto.id)
                lista.push({ produto, cor: grupo.cor })
            }
        }

        return lista
    }, [grupos, selectedCategoryId, busca])

    const nomeCategoria = useMemo(() => {
        if (selectedCategoryId === null) return 'Todos os produtos'
        if (selectedCategoryId === 'DESTAQUE') return 'Destaques'

        return grupos.find((g) => g.id === selectedCategoryId)?.descricao ?? 'Categoria'
    }, [grupos, selectedCategoryId])

    if (produtos.length === 0) {
        return (
            <Stack sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 2, textAlign: 'center' }}>
                <SearchOffIcon sx={{ fontSize: 72 }} />

                <Typography variant="h5" color="text.secondary">
                    {busca
                        ? 'Nenhum produto encontrado para esta busca.'
                        : 'Nenhum produto disponível nesta categoria.'}
                </Typography>
            </Stack>
        )
    }

    return (
        <Stack sx={{ flexGrow: 1, overflow: 'hidden', p: 2, gap: 2, pl:0, pb: 0 }}>
            {mostrarTitulo && (
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', pr: 1 }}>
                    <Typography variant="h3" sx={{ flexGrow: 1, minWidth: 0, fontWeight: 'bold', fontSize: fonteTitulo }}>
                        {busca ? `${produtos.length} ${produtos.length === 1 ? 'resultado' : 'resultados'}` : capitalize(nomeCategoria)}
                    </Typography>

                    {aoBuscar && (
                        <Button
                            size="large"
                            variant="text"
                            startIcon={<SearchIcon />}
                            onClick={aoBuscar}
                            sx={{ flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap', px: 2, textTransform: 'none' }}
                        >
                            Buscar
                        </Button>
                    )}
                </Stack>
            )}

            <Box
                sx={{
                    gap: 1.5,
                    flexGrow: 1,
                    display: 'grid',
                    overflowY: 'auto',
                    alignContent: 'start',
                    gridAutoRows: 'min-content',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${larguraMinimaCard}, 1fr))`,
                    pr: 1,
                    overscrollBehavior: 'contain',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(120, 130, 140, .45) transparent',
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        borderRadius: 8,
                        backgroundColor: 'rgba(120, 130, 140, .45)',
                    },
                }}
            >
                {produtos.map(({ produto, cor }) => renderCard(produto, cor))}
            </Box>
        </Stack>
    )
}

export default memo(GradeProdutos)
